/** Zod schemas for every API boundary. Reused by the Worker (validation) and clients (types). */

import { z } from 'zod';
import {
  COMMENT_MAX_LEN,
  FEED_SORTS,
  MAX_PAGE_SIZE,
  MEDIA_TYPES,
  PROMPT_BODY_MAX_LEN,
  PROMPT_TYPES,
  RATING_MAX,
  RATING_MIN,
  SUBMIT_TITLE_MAX_LEN,
  USER_ROLES,
} from './constants';

/** z.enum over a readonly const tuple. */
function zEnum<T extends string>(values: readonly T[]) {
  return z.enum(values as unknown as [T, ...T[]]);
}

export const promptTypeSchema = zEnum(PROMPT_TYPES);
export const mediaTypeSchema = zEnum(MEDIA_TYPES);
export const feedSortSchema = zEnum(FEED_SORTS);
export const userRoleSchema = zEnum(USER_ROLES);

const cursor = z.string().min(1).max(200).optional();
const limit = z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).optional();

export const cursorQuerySchema = z.object({ cursor, limit });

export const feedQuerySchema = z.object({
  type: promptTypeSchema.optional(),
  model: z.string().min(1).max(64).optional(),
  category: z.string().min(1).max(64).optional(),
  tag: z.string().min(1).max(64).optional(),
  q: z.string().trim().min(1).max(120).optional(),
  language: z.string().min(2).max(8).optional(),
  sort: feedSortSchema.default('trending'),
  /** Canonical platform key, e.g. `civitai.com` or `github.com`. */
  platform: z.string().min(1).max(64).optional(),
  nsfw: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
  cursor,
  limit,
});
export type FeedQuery = z.infer<typeof feedQuerySchema>;

export const searchQuerySchema = z.object({
  q: z.string().trim().min(1).max(120),
  semantic: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
  /**
   * Search the string as typed, with no spelling correction.
   *
   * The zero-result path corrects a misspelling and reports what it did in `correction`, so a
   * client can say "showing results for cyberpunk". That sentence is only honest if the user can
   * take it back — otherwise the offer to search for what they actually typed leads nowhere.
   * Maps to the service's internal `noCorrect`.
   */
  exact: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
  /**
   * Narrow to one prompt type.
   *
   * The route has always honoured this — and used to read it with a bare `c.req.query('type')`
   * *around* the schema, because the schema had no field for it. That is how it was silently
   * dropped once already: an earlier revision parsed the query, ignored the raw param, and a user
   * filtering to "video" got the whole library back. A parameter the endpoint accepts belongs in
   * the object that defines what the endpoint accepts.
   */
  type: z.string().trim().min(1).max(32).optional(),
  cursor,
  limit,
});

export const rateInputSchema = z.object({
  stars: z.coerce.number().int().min(RATING_MIN).max(RATING_MAX),
});
export type RateInput = z.infer<typeof rateInputSchema>;

export const commentCreateSchema = z.object({
  body: z.string().trim().min(1).max(COMMENT_MAX_LEN),
  parentId: z.string().min(1).max(40).optional(),
});
export type CommentCreate = z.infer<typeof commentCreateSchema>;

export const reportSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

export const submitPromptSchema = z.object({
  title: z.string().trim().min(3).max(SUBMIT_TITLE_MAX_LEN),
  body: z.string().trim().min(3).max(PROMPT_BODY_MAX_LEN),
  type: promptTypeSchema.default('other'),
  instructions: z.string().trim().max(4000).optional(),
  negativePrompt: z.string().trim().max(4000).optional(),
  models: z.array(z.string().min(1).max(64)).max(10).optional(),
  categories: z.array(z.string().min(1).max(64)).max(10).optional(),
  tags: z.array(z.string().min(1).max(40)).max(20).optional(),
  sourceUrl: z.string().url().max(500).optional(),
  exampleUrls: z.array(z.string().url().max(1000)).max(10).optional(),
});
export type SubmitPrompt = z.infer<typeof submitPromptSchema>;

export const settingsPatchSchema = z.object({
  language: z.enum(['en', 'fa']).optional(),
  nsfwVisible: z.boolean().optional(),
  notifications: z.boolean().optional(),
});
export type SettingsPatch = z.infer<typeof settingsPatchSchema>;

export const collectionCreateSchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  isPublic: z.boolean().default(false),
});

export const supportMessageSchema = z.object({
  body: z.string().trim().min(1).max(4000),
});

/** ── Ingestion: normalized candidate pushed by the standalone service ─────────────── */

export const ingestMediaSchema = z.object({
  type: mediaTypeSchema,
  url: z.string().url().optional(),
  r2Key: z.string().max(300).optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  duration: z.number().nonnegative().optional(),
  caption: z.string().max(2000).optional(),
  sourceUrl: z.string().url().optional(),
});

export const ingestItemSchema = z.object({
  sourceId: z.string().min(1).max(40),
  externalId: z.string().max(200).optional(),
  title: z.string().max(300).optional(),
  body: z.string().min(1).max(PROMPT_BODY_MAX_LEN),
  type: promptTypeSchema.optional(),
  language: z.string().min(2).max(8).optional(),
  modelHints: z.array(z.string().min(1).max(64)).max(20).optional(),
  categoryHints: z.array(z.string().min(1).max(64)).max(20).optional(),
  tagHints: z.array(z.string().min(1).max(40)).max(40).optional(),
  sourceUrl: z.string().url().max(1000).optional(),
  sourceName: z.string().max(200).optional(),
  authorAttribution: z.string().max(200).optional(),
  isNsfw: z.boolean().optional(),
  dedupeHash: z.string().min(8).max(128),
  media: z.array(ingestMediaSchema).max(20).default([]),
  raw: z.record(z.unknown()).optional(),
  idempotencyKey: z.string().min(1).max(128),
});
export type IngestItem = z.infer<typeof ingestItemSchema>;

export const ingestCursorSchema = z.object({
  sourceId: z.string().min(1).max(40),
  cursor: z.string().max(200),
  stats: z.record(z.unknown()).optional(),
});

/** ── Admin write schemas ─────────────────────────────────────────────────────────── */

export const adminPromptUpsertSchema = z.object({
  id: z.string().min(1).max(40).optional(),
  title: z.string().trim().min(1).max(300),
  body: z.string().trim().min(1).max(PROMPT_BODY_MAX_LEN),
  instructions: z.string().trim().max(8000).optional(),
  negativePrompt: z.string().trim().max(8000).optional(),
  type: promptTypeSchema,
  language: z.string().min(2).max(8).default('en'),
  status: z.enum(['staging', 'published', 'rejected', 'archived']).default('staging'),
  isNsfw: z.boolean().default(false),
  qualityScore: z.number().int().min(0).max(100).optional(),
  sourceUrl: z.string().url().max(1000).optional(),
  sourceName: z.string().max(200).optional(),
  authorAttribution: z.string().max(200).optional(),
  /**
   * Correcting a source number a collector got wrong. `null` clears it.
   *
   * Nullable rather than merely optional because "we do not know" is a state that has to be
   * expressible: a wrong figure is worse than a missing one, since it ranks and it is presented
   * as a fact about somewhere else.
   */
  sourceEngagement: z.number().int().min(0).max(1_000_000_000).nullable().optional(),
  sourceEngagementMetric: z.string().max(40).nullable().optional(),
  modelIds: z.array(z.string()).max(20).optional(),
  categoryIds: z.array(z.string()).max(20).optional(),
  tags: z.array(z.string().min(1).max(40)).max(30).optional(),
});
export type AdminPromptUpsert = z.infer<typeof adminPromptUpsertSchema>;

export const adminUserUpdateSchema = z.object({
  role: userRoleSchema.optional(),
  isBanned: z.boolean().optional(),
  bannedReason: z.string().max(500).optional(),
});

export const broadcastAudienceSchema = z.object({
  all: z.boolean().optional(),
  language: z.enum(['en', 'fa']).optional(),
  activeWithinDays: z.number().int().positive().max(365).optional(),
  pro: z.boolean().optional(),
  /** Explicit recipients — how a test send to one person is expressed. */
  userIds: z.array(z.number().int()).max(1000).optional(),
});

export const broadcastButtonsSchema = z
  .array(z.object({ text: z.string().trim().min(1).max(64), url: z.string().url().max(500) }))
  .max(6);

export const broadcastCreateSchema = z.object({
  body: z.string().trim().min(1).max(4000),
  audience: broadcastAudienceSchema.default({ all: true }),
  buttons: broadcastButtonsSchema.optional(),
  scheduledAt: z.number().int().optional(),
});

export const broadcastUpdateSchema = z.object({
  body: z.string().trim().min(1).max(4000).optional(),
  audience: broadcastAudienceSchema.optional(),
  buttons: broadcastButtonsSchema.optional(),
  scheduledAt: z.number().int().nullable().optional(),
});

/** ── Admin dashboard v2 ──────────────────────────────────────────────────────────────── */

export const moderationDecisionSchema = z.object({
  status: z.enum(['published', 'rejected', 'staging', 'archived']),
  reason: z.string().trim().max(500).optional(),
});

export const bulkDecisionSchema = z.object({
  ids: z.array(z.string().min(1).max(40)).min(1).max(200),
  status: z.enum(['published', 'rejected', 'staging', 'archived']),
  reason: z.string().trim().max(500).optional(),
});

export const promptEditSchema = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  body: z.string().trim().min(1).max(PROMPT_BODY_MAX_LEN).optional(),
  instructions: z.string().trim().max(8000).nullable().optional(),
  negativePrompt: z.string().trim().max(8000).nullable().optional(),
  type: promptTypeSchema.optional(),
  language: z.string().min(2).max(8).optional(),
  isNsfw: z.boolean().optional(),
  modelIds: z.array(z.string().min(1).max(40)).max(20).optional(),
  categoryIds: z.array(z.string().min(1).max(40)).max(20).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(30).optional(),
  authorAttribution: z.string().trim().max(200).nullable().optional(),
  sourceUrl: z.string().url().max(1000).nullable().optional(),
});

export const directMessageSchema = z.object({
  body: z.string().trim().min(1).max(4000),
  buttons: broadcastButtonsSchema.optional(),
  photoUrl: z.string().url().max(1000).optional(),
  /** Applies `{{first_name}}`-style substitution before sending. */
  fillVariables: z.boolean().optional(),
});

export const adminNoteSchema = z.object({
  body: z.string().trim().min(1).max(4000),
  tags: z.array(z.string().trim().min(1).max(40)).max(10).optional(),
});

export const userAdminPatchSchema = z.object({
  role: userRoleSchema.optional(),
  isBanned: z.boolean().optional(),
  bannedReason: z.string().trim().max(500).nullable().optional(),
  /** Days; omitted or null bans indefinitely. */
  banDays: z.number().int().positive().max(3650).nullable().optional(),
  isPro: z.boolean().optional(),
  proDays: z.number().int().positive().max(3650).nullable().optional(),
});

export const threadPatchSchema = z.object({
  status: z.enum(['open', 'pending', 'closed']).optional(),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
  assignedAdminId: z.number().int().nullable().optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(10).optional(),
});

export const cannedResponseSchema = z.object({
  title: z.string().trim().min(1).max(120),
  body: z.string().trim().min(1).max(4000),
});

export const curationSchema = z.object({
  promptOfTheDay: z.string().min(1).max(40).nullable().optional(),
  featured: z.array(z.string().min(1).max(40)).max(50).optional(),
  railOrder: z.array(z.string().min(1).max(64)).max(20).optional(),
});

export const channelPostSchema = z.object({
  promptId: z.string().min(1).max(40),
  caption: z.string().trim().max(4000).optional(),
  scheduledAt: z.number().int().nullable().optional(),
});

export const settingsUpdateSchema = z.object({
  key: z.string().trim().min(1).max(64),
  value: z.unknown(),
});

export const featureFlagsSchema = z.object({
  commentsEnabled: z.boolean().optional(),
  submissionsEnabled: z.boolean().optional(),
  nsfwDefaultVisible: z.boolean().optional(),
  monetizationEnabled: z.boolean().optional(),
  maintenanceMode: z.boolean().optional(),
  autoPublishTrustedSources: z.boolean().optional(),
  /** Global kill switch for every per-source approval policy. Off unless deliberately set. */
  autoApprovalEnabled: z.boolean().optional(),
  /** Channel the scheduled cadence posts to. Absent means channel posting is inert. */
  channelId: z.string().trim().max(120).optional(),
  channelPostsPerDay: z.number().int().min(0).max(48).optional(),
  submitRateLimitPerDay: z.number().int().min(0).max(1000).optional(),
});
export type FeatureFlags = z.infer<typeof featureFlagsSchema>;

/** ── Bulk approval engine ────────────────────────────────────────────────────────────── */

export const selectionFiltersSchema = z.object({
  status: z.string().max(20).optional(),
  source: z.string().max(200).optional(),
  type: promptTypeSchema.optional(),
  model: z.string().max(64).optional(),
  category: z.string().max(64).optional(),
  language: z.string().min(2).max(8).optional(),
  hasMedia: z.boolean().optional(),
  needsReview: z.boolean().optional(),
  minConfidence: z.number().int().min(0).max(100).optional(),
  maxConfidence: z.number().int().min(0).max(100).optional(),
  from: z.number().int().optional(),
  to: z.number().int().optional(),
  q: z.string().trim().max(120).optional(),
  /** Explicit ids, when rows were picked by hand rather than by filter. */
  ids: z.array(z.string().min(1).max(40)).max(2000).optional(),
});

export const exclusionSettingsSchema = z.object({
  qualityFlag: z.boolean().optional(),
  noMedia: z.boolean().optional(),
  lowConfidence: z.boolean().optional(),
  nearDuplicate: z.boolean().optional(),
  nsfw: z.boolean().optional(),
  noModel: z.boolean().optional(),
  noCategory: z.boolean().optional(),
  minConfidence: z.number().int().min(0).max(100).optional(),
});

const channelModeSchema = z.enum(['queue', 'skip', 'top']);

export const bulkPreviewSchema = z.object({
  filters: selectionFiltersSchema,
  exclusions: exclusionSettingsSchema.optional(),
  channelMode: channelModeSchema.optional(),
  channelTopN: z.number().int().min(1).max(1000).optional(),
});

export const bulkJobCreateSchema = z.object({
  filters: selectionFiltersSchema,
  targetStatus: z.enum(['published', 'rejected', 'archived', 'staging']),
  reason: z.string().trim().max(500).optional(),
  exclusions: exclusionSettingsSchema.optional(),
  channelMode: channelModeSchema.optional(),
  channelTopN: z.number().int().min(1).max(1000).optional(),
});

export const approvalPolicySchema = z.object({
  mode: z.enum(['manual', 'auto-approve', 'auto-approve-if']),
  conditions: z
    .object({
      minConfidence: z.number().int().min(0).max(100).optional(),
      requireMedia: z.boolean().optional(),
      requireModel: z.boolean().optional(),
      requireCategory: z.boolean().optional(),
      requireNoQualityFlags: z.boolean().optional(),
      rejectNsfw: z.boolean().optional(),
      minBodyLength: z.number().int().min(0).max(20000).optional(),
      maxBodyLength: z.number().int().min(0).max(20000).optional(),
    })
    .optional(),
});

/** ── Curation ────────────────────────────────────────────────────────────────────────── */

export const railSchema = z.object({
  id: z.string().trim().min(1).max(40),
  title: z.string().trim().min(1).max(60),
  titleFa: z.string().trim().max(60).optional(),
  source: z.enum([
    'trending',
    'new',
    'top_rated',
    'most_downloaded',
    'category',
    'model',
    'manual',
    'collection',
  ]),
  value: z.string().trim().max(64).optional(),
  promptIds: z.array(z.string().min(1).max(40)).max(60).optional(),
  /**
   * The **pool**: how many cards the cron ranks and caches for this rail.
   *
   * This used to mean "how many the screen shows", and V10 turned the rails into carousels
   * without renaming it — so a curator setting 12 was quietly setting the size of the cached
   * pool, and the client then asked for 24 and got whatever was there. The field kept its name
   * because renaming it would orphan every stored config; what changed is that `windowSize`
   * now exists to mean the thing `limit` used to.
   */
  limit: z.number().int().min(1).max(60),
  /**
   * How many of the pool a single visit shows, before the user scrolls the carousel.
   *
   * Smaller than `limit` on purpose: the gap between them is what rotation moves through, and a
   * window equal to the pool is a carousel that shows the same cards every time.
   */
  windowSize: z.number().int().min(4).max(60).optional(),
  /**
   * Whether the window starts at a different place each visit.
   *
   * The owner's ask — "the top of each section should open randomly to give the user the feeling
   * prompts are always updating". Off is a legitimate choice for a hand-picked rail, where the
   * curator means a specific order and rotating it discards the decision.
   */
  rotate: z.boolean().optional(),
  visible: z.boolean(),
  language: z.enum(['en', 'fa']).optional(),
});

export const curationConfigSchema = z.object({
  rails: z.array(railSchema).max(20).optional(),
  collections: z
    .array(
      z.object({
        id: z.string().trim().min(1).max(40),
        title: z.string().trim().min(1).max(80),
        description: z.string().trim().max(400).optional(),
        coverUrl: z.string().url().max(1000).optional(),
        promptIds: z.array(z.string().min(1).max(40)).max(100),
      }),
    )
    .max(20)
    .optional(),
  pins: z
    .array(
      z.object({
        promptId: z.string().min(1).max(40),
        railId: z.string().min(1).max(40),
        kind: z.enum(['pin', 'exclude']),
        expiresAt: z.number().int().optional(),
      }),
    )
    .max(200)
    .optional(),
  categoryChips: z.array(z.string().min(1).max(64)).max(20).optional(),
  promptOfTheDay: z.record(z.string().min(1).max(40)).optional(),
  potdFallback: z.enum(['top_rated', 'trending', 'newest', 'none']).optional(),
  announcement: z
    .object({
      text: z.string().trim().min(1).max(300),
      textFa: z.string().trim().max(300).optional(),
      url: z.string().url().max(1000).optional(),
      startsAt: z.number().int().optional(),
      endsAt: z.number().int().optional(),
    })
    .nullable()
    .optional(),
});

export const rankingWeightsSchema = z.object({
  downloadWeight: z.number().min(0).max(100).optional(),
  ratingWeight: z.number().min(0).max(1000).optional(),
  recencyWeight: z.number().min(0).max(1000).optional(),
  halfLifeDays: z.number().min(0.5).max(365).optional(),
  minRatingCount: z.number().int().min(0).max(100).optional(),
  excluded: z
    .array(z.object({ promptId: z.string().min(1).max(40), reason: z.string().max(200) }))
    .max(200)
    .optional(),
  pinned: z.array(z.string().min(1).max(40)).max(20).optional(),
});

export const ingestionSourcePatchSchema = z.object({
  isActive: z.boolean().optional(),
  trusted: z.boolean().optional(),
  confidenceThreshold: z.number().min(0).max(1).optional(),
  maxItems: z.number().int().min(1).max(5000).optional(),
  scheduleCron: z.string().trim().max(64).nullable().optional(),
});

export const ingestionSourceSchema = z.object({
  kind: z.enum(['telegram_channel', 'website']),
  identifier: z.string().min(1).max(300),
  config: z.record(z.unknown()).optional(),
  isActive: z.boolean().default(true),
});

/** Standard API envelope. */
export interface ApiEnvelope<T> {
  ok: boolean;
  data?: T;
  error?: { code: string; message: string };
  requestId?: string;
}
